export type SettingsNavigationScope = 'personal' | 'workspace' | 'archived';

export interface SettingsNavigationState {
  scope: SettingsNavigationScope;
  slug: string;
}

export interface SettingsNavigationPermissions {
  canManageBilling: boolean;
  canViewStorageLimits: boolean;
  canViewBilling: boolean;
  canViewAnalytics?: boolean;
  showFilePermissions: boolean;
}

export interface SettingsNavigationItem {
  href: string;
  icon:
    | 'app-permissions'
    | 'connection-access'
    | 'archive'
    | 'profile'
    | 'preferences'
    | 'security'
    | 'workspaces'
    | 'workspace'
    | 'members'
    | 'permissions'
    | 'memories'
    | 'usage'
    | 'storage'
    | 'billing';
  label:
    | 'appPermissions'
    | 'connectionAccess'
    | 'archivedChats'
    | 'profile'
    | 'preferences'
    | 'security'
    | 'workspaces'
    | 'general'
    | 'membersTeams'
    | 'permissions'
    | 'memories'
    | 'usage'
    | 'storage'
    | 'billing'
    | 'appearance'
    | 'summarization'
    | 'notifications'
    | 'invitations'
    | 'teams'
    | 'roles'
    | 'fileAccess'
    | 'myMemories'
    | 'sharedMemories'
    | 'overview'
    | 'creditActivity'
    | 'invoices'
    | 'pricing';
  searchTerms: readonly string[];
  slug: string;
}

// Temporarily hide entry points while keeping the existing Memories pages.
const MEMORIES_SETTINGS_VISIBLE = false;

const PERSONAL_ALIASES: Record<string, string> = {
  appearance: 'preferences',
  summarization: 'preferences',
  invitations: 'organizations',
};
const WORKSPACE_ALIASES: Record<string, string> = {
  teams: 'members',
  roles: 'permissions',
  'file-permissions': 'permissions',
  analytics: 'usage',
  invoices: 'billing',
  privacy: 'workspace',
  purchase: 'billing',
  pricing: 'billing',
};

export function getSettingsNavigationState(
  pathname: string | null
): SettingsNavigationState {
  const parts = pathname?.split('/').filter(Boolean) ?? [];
  if (parts[0] === 'settings') {
    const slug = parts[1] ?? 'profile';
    return { scope: 'personal', slug: PERSONAL_ALIASES[slug] ?? slug };
  }
  if (parts[1] === 'settings' && parts[2] === 'profile')
    return { scope: 'personal', slug: 'profile' };
  const index = parts.indexOf('settings');
  const slug = index >= 0 ? (parts[index + 1] ?? 'workspace') : '';
  return {
    scope: slug === 'archived-chats' ? 'archived' : 'workspace',
    slug: WORKSPACE_ALIASES[slug] ?? slug,
  };
}

function item(
  prefix: string,
  slug: string,
  label: SettingsNavigationItem['label'],
  icon: SettingsNavigationItem['icon']
): SettingsNavigationItem {
  return { href: `${prefix}/${slug}`, slug, label, icon, searchTerms: [] };
}

export function getPersonalSettingsNavigationItems(): SettingsNavigationItem[] {
  return [
    item('/settings', 'profile', 'profile', 'profile'),
    item('/settings', 'preferences', 'preferences', 'preferences'),
    item('/settings', 'security', 'security', 'security'),
    item('/settings', 'organizations', 'workspaces', 'workspaces'),
  ];
}

export function getWorkspaceSettingsNavigationItems({
  orgSlug,
  permissions,
}: {
  orgSlug: string | null;
  permissions: SettingsNavigationPermissions;
}): SettingsNavigationItem[] {
  if (!orgSlug) return [];
  const prefix = `/${orgSlug}/settings`;
  return [
    item(prefix, 'workspace', 'general', 'workspace'),
    item(prefix, 'members', 'membersTeams', 'members'),
    item(prefix, 'permissions', 'permissions', 'permissions'),
    {
      ...item(prefix, 'app-permissions', 'appPermissions', 'app-permissions'),
      searchTerms: ['apps', 'always allow', 'consents', 'revoke', 'app access'],
    },
    {
      ...item(
        prefix,
        'connection-access',
        'connectionAccess',
        'connection-access'
      ),
      searchTerms: [
        'connections',
        'gmail',
        'calendar',
        'slack',
        'admin access',
        'grants',
      ],
    },
    ...(MEMORIES_SETTINGS_VISIBLE
      ? [item(prefix, 'memories', 'memories', 'memories')]
      : []),
    item(prefix, 'usage', 'usage', 'usage'),
    ...(permissions.canViewStorageLimits
      ? [item(prefix, 'storage', 'storage', 'storage')]
      : []),
    ...(permissions.canViewBilling || permissions.canManageBilling
      ? [item(prefix, 'billing', 'billing', 'billing')]
      : []),
  ];
}

/** Search exposes the relevant tab, while the sidebar stays at destination level. */
export function getSettingsSubpageSearchItems({
  orgSlug,
  permissions,
}: {
  orgSlug: string | null;
  permissions: SettingsNavigationPermissions;
}): SettingsNavigationItem[] {
  const tab = (
    prefix: string,
    slug: string,
    value: string,
    label: SettingsNavigationItem['label'],
    icon: SettingsNavigationItem['icon']
  ) => ({
    ...item(prefix, slug, label, icon),
    href: `${prefix}/${slug}?tab=${value}`,
  });
  const personal = [
    tab('/settings', 'preferences', 'general', 'appearance', 'preferences'),
    tab(
      '/settings',
      'preferences',
      'assistant',
      'summarization',
      'preferences'
    ),
    tab(
      '/settings',
      'preferences',
      'notifications',
      'notifications',
      'preferences'
    ),
    tab(
      '/settings',
      'organizations',
      'invitations',
      'invitations',
      'workspaces'
    ),
  ];
  if (!orgSlug) return personal;
  const prefix = `/${orgSlug}/settings`;
  return [
    ...personal,
    tab(prefix, 'members', 'teams', 'teams', 'members'),
    tab(prefix, 'permissions', 'roles', 'roles', 'permissions'),
    ...(permissions.showFilePermissions
      ? [tab(prefix, 'permissions', 'files', 'fileAccess', 'permissions')]
      : []),
    ...(MEMORIES_SETTINGS_VISIBLE
      ? [
          tab(prefix, 'memories', 'personal', 'myMemories', 'memories'),
          tab(prefix, 'memories', 'shared', 'sharedMemories', 'memories'),
        ]
      : []),
    tab(prefix, 'usage', 'overview', 'overview', 'usage'),
    ...(permissions.canViewBilling
      ? [
          tab(prefix, 'usage', 'activity', 'creditActivity', 'usage'),
          tab(prefix, 'billing', 'invoices', 'invoices', 'billing'),
        ]
      : []),
    ...(permissions.canManageBilling
      ? [item(prefix, 'purchase', 'pricing', 'billing')]
      : []),
  ];
}

export function getArchivedSettingsNavigationItems(
  orgSlug: string | null
): SettingsNavigationItem[] {
  return orgSlug
    ? [
        item(
          `/${orgSlug}/settings`,
          'archived-chats',
          'archivedChats',
          'archive'
        ),
      ]
    : [];
}

export function resolveSettingsTab(
  tabs: readonly { value: string }[],
  requested: string | null
) {
  return tabs.find((tab) => tab.value === requested)?.value ?? tabs[0]?.value;
}
